import type { MouseEvent, ReactNode } from "react";

interface ButtonProps {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}

const variantClasses = {
  primary: "bg-primary text-white hover:bg-primary-hover",
  secondary: "border border-border-strong bg-surface text-text-primary hover:bg-bg",
  danger: "bg-danger text-white hover:opacity-90",
  ghost: "text-text-secondary hover:bg-bg",
} as const;

const sizeClasses = {
  sm: "min-h-11 px-3 text-sm md:h-9 md:min-h-9",
  md: "h-11 min-h-11 px-4 text-sm",
  lg: "h-12 min-h-12 px-6 text-base",
} as const;

export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  disabled = false,
  type = "button",
  onClick,
  children,
  className,
  ariaLabel,
}: ButtonProps): JSX.Element {
  const pending = loading || disabled;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={pending}
      aria-label={ariaLabel}
      aria-busy={loading}
      className={cx(
        buttonBase,
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && "w-full",
        className,
      )}
    >
      {loading && <Spinner />}
      <span>{children}</span>
    </button>
  );
}

function Spinner(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

const buttonBase =
  "inline-flex items-center justify-center gap-2 rounded-md font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] disabled:cursor-not-allowed disabled:opacity-60";

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}
